import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { GOLD, SILVER, MUTED, BORDER, GREEN, RED, TEXT, CHART_COLORS, METAL_TYPES } from '../constants';
import { fmt, fmtDec, today } from '../utils';
import { SectionTitle, KPICard, ChartCard, Card, Table, TR, TD, EmptyState, Badge } from './UI';

export default function InventoryTab({ transactions, metalPrices }) {
  // Build net inventory map
  const invMap = {};
  transactions.forEach((t) => {
    const key = `${t.metal || 'Other'}||${t.category || 'Other'}||${t.description || ''}`;
    if (!invMap[key]) {
      invMap[key] = {
        metal: t.metal || 'Other',
        category: t.category || 'Other',
        description: t.description || '',
        netQty: 0, totalCost: 0, totalRevenue: 0, netWeight: 0,
        buyCount: 0, sellCount: 0,
      };
    }
    const r   = invMap[key];
    const qty = t.qty || 1;
    const amt = t.totalAmount || 0;
    const wt  = (t.weight || 0) * qty;
    if (t.direction === 'Purchase') {
      r.netQty += qty; r.totalCost += amt; r.netWeight += wt; r.buyCount++;
    } else {
      r.netQty -= qty; r.totalRevenue += amt; r.netWeight -= wt; r.sellCount++;
    }
  });

  const rows = Object.values(invMap).sort((a, b) => Math.abs(b.netQty) - Math.abs(a.netQty));

  // Metal weight summary
  const metalSummary = METAL_TYPES.map((m) => {
    const relRows  = rows.filter(r => r.metal === m);
    const netOz    = relRows.reduce((s, r) => s + r.netWeight, 0);
    const cost     = relRows.reduce((s, r) => s + r.totalCost, 0);
    const revenue  = relRows.reduce((s, r) => s + r.totalRevenue, 0);
    const spot     = metalPrices[m];
    const spotVal  = spot ? netOz * spot : null;
    const pl       = revenue - cost;
    return { metal: m, netOz, cost, revenue, pl, spot, spotVal };
  });

  const totalSpotValue  = metalSummary.reduce((s, m) => s + (m.spotVal || 0), 0);
  const totalCost       = metalSummary.reduce((s, m) => s + m.cost, 0);
  const totalRevenue    = metalSummary.reduce((s, m) => s + m.revenue, 0);
  const totalPL         = totalRevenue - totalCost;

  // Chart data
  const chartData = metalSummary.map(m => ({
    metal: m.metal,
    'Net oz': parseFloat(m.netOz.toFixed(3)),
    'Spot Value': m.spotVal ? parseFloat(m.spotVal.toFixed(0)) : 0,
  }));

  const tooltipStyle = { contentStyle: { background: '#111418', border: `1px solid #1E2530`, fontSize: 11, borderRadius: 6 } };

  return (
    <div>
      <SectionTitle title="Inventory Position" sub={`Net from all transactions through ${today()}`} />

      {/* Spot value cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 }}>
        {metalSummary.map((m) => (
          <KPICard
            key={m.metal}
            label={m.metal}
            value={`${m.netOz.toFixed(3)} oz`}
            sub={m.spot ? `@ ${fmtDec(m.spot)} = ${fmt(m.spotVal)}` : 'Spot unavailable'}
            color={GOLD}
          />
        ))}
      </div>

      {/* Summary row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        <KPICard label="Total Spot Value"    value={totalSpotValue > 0 ? fmt(totalSpotValue) : '—'} sub="all metals at spot" color={GOLD} />
        <KPICard label="Total Cost Basis"    value={fmt(totalCost)}    sub="all purchases"  color={RED} />
        <KPICard label="Total Sales Revenue" value={fmt(totalRevenue)} sub="all sales"      color={GREEN} />
        <KPICard label="Realized P/L"        value={fmt(totalPL)}      sub="sold items"     color={totalPL >= 0 ? GREEN : RED} />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <ChartCard title="Net Weight by Metal (oz)">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="metal" stroke={MUTED} tick={{ fontSize: 10 }} />
              <YAxis stroke={MUTED} tick={{ fontSize: 9 }} tickFormatter={v=>`${v}oz`} />
              <Tooltip {...tooltipStyle} formatter={v=>`${v} oz`} />
              <Bar dataKey="Net oz" fill={GOLD} radius={[3,3,0,0]}>
                {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Estimated Spot Value by Metal ($)">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="metal" stroke={MUTED} tick={{ fontSize: 10 }} />
              <YAxis stroke={MUTED} tick={{ fontSize: 9 }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
              <Tooltip {...tooltipStyle} formatter={v=>fmt(v)} />
              <Bar dataKey="Spot Value" fill={SILVER} radius={[3,3,0,0]}>
                {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Detail table */}
      <SectionTitle title="Position Detail" sub="By metal, category, and description" />
      {rows.length === 0 ? (
        <EmptyState text="No inventory data — enter transactions to populate" />
      ) : (
        <Table headers={['Metal','Category','Description','Net Qty','Net Weight','Avg Cost','Total Cost','Revenue','Realized P/L']}>
          {rows.map((r, i) => {
            const pl      = r.totalRevenue - r.totalCost;
            const avgCost = r.buyCount > 0 && r.netQty !== 0 ? r.totalCost / (r.buyCount > 0 ? r.netQty + (r.sellCount > 0 ? r.netQty : 0) : r.netQty) : 0;
            return (
              <TR key={i}>
                <TD>{r.metal}</TD>
                <TD style={{ color: MUTED }}>{r.category}</TD>
                <TD style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description || '—'}</TD>
                <TD style={{ color: r.netQty > 0 ? GREEN : r.netQty < 0 ? RED : MUTED }}>{r.netQty > 0 ? `+${r.netQty}` : r.netQty}</TD>
                <TD style={{ color: MUTED }}>{r.netWeight.toFixed(4)} oz</TD>
                <TD>{fmt(r.totalCost / Math.max(r.buyCount, 1))}</TD>
                <TD>{fmt(r.totalCost)}</TD>
                <TD>{fmt(r.totalRevenue)}</TD>
                <TD style={{ color: pl >= 0 ? GREEN : RED, fontWeight: 500 }}>{fmt(pl)}</TD>
              </TR>
            );
          })}
        </Table>
      )}
    </div>
  );
}
